import { addDays, dateToIso, startOfWeek, todayIso } from './calendar-events';

// The classic calendar navigates with a bare integer offset whose unit is the
// active view filter (days / weeks / months, resolved by
// ShiftsCalendarFilterable on the backend). Converting between units keeps the
// displayed period when the user changes the view instead of snapping back to
// the current one.
export type CalendarView = 'today' | 'weekly' | 'monthly';

const KNOWN_VIEWS: readonly string[] = ['today', 'weekly', 'monthly'];
const DAYS_PER_WEEK = 7;
const MONTHS_PER_YEAR = 12;
const MS_PER_DAY = 86_400_000;

function utcValue(date: string): number {
  const [year, month, day] = date.split('-').map(Number);

  return Date.UTC(year, month - 1, day);
}

function daysBetween(from: string, to: string): number {
  return Math.round((utcValue(to) - utcValue(from)) / MS_PER_DAY);
}

function startOfMonth(date: string, monthDelta = 0): string {
  const [year, month] = date.split('-').map(Number);

  return dateToIso(new Date(year, month - 1 + monthDelta, 1));
}

// First day of the period the backend renders for (view, offset).
function periodStart(view: CalendarView, offset: number, today: string): string {
  if (view === 'today') return addDays(today, offset);
  if (view === 'weekly') return addDays(startOfWeek(today), offset * DAYS_PER_WEEK);

  return startOfMonth(today, offset);
}

// Offset whose (view, offset) period contains the anchor date.
function offsetFor(view: CalendarView, anchor: string, today: string): number {
  if (view === 'today') return daysBetween(today, anchor);
  if (view === 'weekly') return daysBetween(startOfWeek(today), startOfWeek(anchor)) / DAYS_PER_WEEK;

  const [anchorYear, anchorMonth] = anchor.split('-').map(Number);
  const [todayYear, todayMonth] = today.split('-').map(Number);

  return ((anchorYear - todayYear) * MONTHS_PER_YEAR) + (anchorMonth - todayMonth);
}

function isKnownView(view: string | undefined): view is CalendarView {
  return !!view && KNOWN_VIEWS.includes(view);
}

// First day of the period the calendar shows for (view, offset); undefined
// when the view is unknown.
export function periodStartDate(
  view: string | undefined,
  offset: number,
  today: string = todayIso(),
): string | undefined {
  if (!isKnownView(view)) return undefined;

  return periodStart(view, offset, today);
}

// Offset whose (view, offset) period contains the given date.
export function offsetContainingDate(
  view: string | undefined,
  date: string,
  today: string = todayIso(),
): number {
  if (!isKnownView(view)) return 0;

  return offsetFor(view, date, today);
}

export function convertOffsetBetweenViews(
  fromView: string | undefined,
  toView: string | undefined,
  offset: number,
  today: string = todayIso(),
): number {
  // No conversion is possible without both units (e.g. the very first store
  // sync, where the persisted view is still undefined): keep the offset, the
  // filters were already navigating in the destination unit.
  if (fromView === toView || !isKnownView(fromView) || !isKnownView(toView)) return offset;

  return offsetFor(toView, periodStart(fromView, offset, today), today);
}
